import axios from "axios";
import { isDate, parseISO, parse } from "date-fns";
import { useDispatch } from "react-redux";

import useSystem from "./useSystem";
import useErrorTracking from "./useErrorTracking";
import { errorActions } from "../store/error";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

const axiosExternal = axios.create();

const useApi = () => {
  const { setCurrentVersion } = useSystem();
  const dispatch = useDispatch();
  const { logNetworkError } = useErrorTracking();

  const isAbortLikeError = (error) => {
    const message = String(error?.message || "").toLowerCase();
    return (
      error?.code === "ERR_CANCELED" ||
      message.includes("aborted") ||
      message.includes("canceled")
    );
  };

  const shouldSkipNetworkLog = (error, status) => {
    if (Number(status) === 304) {
      return true;
    }

    return isAbortLikeError(error);
  };

  const buildLogEndpoint = (args, defaultAction) => {
    const path = requestPath(args);
    if (path) {
      return path;
    }

    const entityName = String(args?.entityName || "UnknownEntity");
    const actionName = String(args?.action || defaultAction || "unknown");
    return `entity:${entityName};action:${actionName}`;
  };

  const requestHeader = (args, defaultAction) => {
    const token = localStorage.getItem("token");

    let headers = {
      action: args.action || defaultAction,
      entityName: args.entityName,
      options: args.options ? JSON.stringify(args.options) : null,
      pageNumber: args.pageNumber || null,
      recordsPerPage: args.recordsPerPage || null,
      pipeline: args.pipeline ? args.pipeline : "standard",
      orderBy: args.orderBy || null,
      order: args.order || null,
    };

    if (token) {
      headers.authorization = `Bearer ${token}`;
    }

    if (!!args.filter) {
      headers.filter = JSON.stringify(args.filter);
    }

    if (args.file) {
      headers["Content-Type"] = "multipart/form-data";
    }

    return headers;
  };

  const requestPath = (args) => {
    let url = args.url || "";

    if (args.file) {
      url += "/upload";
    }

    let path = !!args.id ? `${url}/${args.id}` : url;

    return path;
  };

  const getData = async (args) => {
    let results = { isSuccessful: false, message: "" };
    try {
      // Handle legacy calls with string parameters
      if (typeof args === "string") {
        console.warn(
          "getData called with legacy string parameter, please update to object format",
        );
        return results;
      }

      const response = await axiosInstance.get(requestPath(args), {
        headers: requestHeader(args, "get"),
      });

      if (response.status === 200) {
        results = await serverResponse(response);
      }
    } catch (err) {
      const status = err.response?.status;
      if (!shouldSkipNetworkLog(err, status)) {
        await logNetworkError(err, buildLogEndpoint(args, "get"), {
          entityName: args.entityName,
          action: "get",
          status,
        });
      }
      console.error("getData error:", {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
      });
      const responseMessage = err.response?.data?.message;
      const responseError = err.response?.data?.error;
      const responseData = err.response?.data;
      const fallbackMessage = err.message;
      results.message =
        responseMessage ||
        responseError ||
        (typeof responseData === "string"
          ? responseData
          : responseData
            ? JSON.stringify(responseData)
            : fallbackMessage);
    }

    return results;
  };

  const postData = async (args) => {
    let results = { isSuccessful: false, message: "" };
    const defaultAction = args.data && args.data.id ? "update" : "add";

    try {
      // Extract filter from data if it exists (for actions like fullList passed via data.filter)
      const filterToLog = args.filter || (args.data && args.data.filter);

      const response = await axiosInstance.post(
        requestPath(args),
        { data: JSON.stringify(args.data) },
        { headers: requestHeader(args, defaultAction) },
      );

      results = await serverResponse(response);
    } catch (err) {
      const status = err.response?.status;
      if (!shouldSkipNetworkLog(err, status)) {
        await logNetworkError(err, buildLogEndpoint(args, defaultAction), {
          entityName: args.entityName,
          action: args.action || defaultAction,
          status,
        });
      }
      console.error("postData error:", {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
      });
      const responseMessage = err.response?.data?.message;
      const responseError = err.response?.data?.error;
      const responseData = err.response?.data;
      const fallbackMessage = err.message || "Request failed";
      results.message =
        responseMessage ||
        responseError ||
        (typeof responseData === "string"
          ? responseData
          : responseData
            ? JSON.stringify(responseData)
            : fallbackMessage);
    }

    return results;
  };

  const deleteData = async (args) => {
    let results = { isSuccessful: false, message: "" };

    try {
      let defaultAction = "delete";

      const response = await axiosInstance.post(
        requestPath(args),
        { data: JSON.stringify(args.data) },
        {
          headers: requestHeader(args, defaultAction),
        },
      );

      results = await serverResponse(response);
    } catch (err) {
      const status = err.response?.status;
      if (!shouldSkipNetworkLog(err, status)) {
        await logNetworkError(err, buildLogEndpoint(args, "delete"), {
          entityName: args.entityName,
          action: "delete",
          status,
        });
      }
      console.error("deleteData error:", err);
      const responseMessage = err.response?.data?.message;
      const responseError = err.response?.data?.error;
      const responseData = err.response?.data;
      const fallbackMessage = err.message || "Delete failed";
      results.message =
        responseMessage ||
        responseError ||
        (typeof responseData === "string"
          ? responseData
          : responseData
            ? JSON.stringify(responseData)
            : fallbackMessage);
    }

    return results;
  };

  const upload = async (args, uploadProgress) => {
    let results = { isSuccessful: false, message: "" };

    try {
      let defaultAction = "upload";

      const data = {
        ...args.data,
        type: args.file.type,
      };

      let formData = new FormData();
      formData.append("file", args.file);
      formData.append("data", JSON.stringify(data));

      const response = await axiosInstance.post(requestPath(args), formData, {
        headers: requestHeader(args, defaultAction),
        onUploadProgress: (e) => {
          if (!uploadProgress) return;

          const progress = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
          uploadProgress(progress);
        },
      });

      results = await serverResponse(response);
    } catch (err) {
      const status = err.response?.status;
      if (!shouldSkipNetworkLog(err, status)) {
        await logNetworkError(err, buildLogEndpoint(args, "upload"), {
          entityName: args.entityName,
          action: "upload",
          status,
        });
      }
      console.error("upload error:", err);
      const responseMessage = err.response?.data?.message;
      const responseError = err.response?.data?.error;
      const responseData = err.response?.data;
      const fallbackMessage = err.message || "Upload failed";
      results.message =
        responseMessage ||
        responseError ||
        (typeof responseData === "string"
          ? responseData
          : responseData
            ? JSON.stringify(responseData)
            : fallbackMessage);
    }

    return results;
  };

  // uploadFile.js
  const uploadFile = async (file, metadata) => {
    try {
      // Derive extension from file.name if available
      let extension = "";
      if (file.name && file.name.includes(".")) {
        extension = file.name.substring(file.name.lastIndexOf("."));
      } else {
        extension = ".dat"; // fallback
      }

      const filename = `${metadata.key}${extension}`;

      const formData = new FormData();
      formData.append("file", file, filename); // enforce key + extension

      // Append metadata fields
      Object.entries(metadata).forEach(([field, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(field, value);
        }
      });

      const response = await fetch(`${process.env.REACT_APP_SPACES}/upload`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();

      const responseFullKey =
        typeof result?.fullKey === "string" && result.fullKey.length > 0
          ? result.fullKey
          : null;

      const derivedFullKey =
        responseFullKey ||
        (result?.folder && result?.key
          ? `${result.folder}/${result.key}`
          : null);

      const derivedFileName =
        (derivedFullKey && derivedFullKey.split("/").pop()) ||
        (typeof result?.key === "string" && result.key.includes("/")
          ? result.key.split("/").pop()
          : result?.key) ||
        filename;

      return {
        ...result,
        fullKey: derivedFullKey || result?.fullKey,
        fileName: derivedFileName,
      };
    } catch (err) {
      await logNetworkError(err, `${process.env.REACT_APP_SPACES}/upload`, {
        action: "uploadFile",
        metadata: metadata?.key,
      });
      console.error("Error uploading file:", err);
      throw err;
    }
  };

  // retrieveFile.js - accepts "folder/key" or separate args
  // Uses /image for survey-images (no Document record), /file for others
  const retrieveFile = async (keyOrFolder, maybeKey) => {
    let folder, key;
    try {
      if (
        !maybeKey &&
        typeof keyOrFolder === "string" &&
        /^(https?:|data:|blob:)/i.test(keyOrFolder)
      ) {
        return keyOrFolder;
      }

      if (maybeKey) {
        folder = keyOrFolder;
        key = maybeKey;
      } else {
        const str = String(keyOrFolder || "");
        if (str.includes("/")) {
          const parts = str.split("/");
          folder = parts[0];
          key = parts.slice(1).join("/");
        } else {
          // Backward compatibility: treat bare filenames as survey-images keys.
          folder = "survey-images";
          key = str;
        }
      }

      if (!folder || !key) {
        throw new Error("Missing folder or key for retrieveFile");
      }

      const route = folder === "survey-images" ? "image" : "file";
      const response = await fetch(
        `${process.env.REACT_APP_SPACES}/${route}/${folder}/${key}`,
      );
      if (!response.ok) {
        const retrieveError = new Error(
          `Retrieve failed: ${response.statusText || response.status}`,
        );
        retrieveError.status = response.status;
        throw retrieveError;
      }

      const { url } = await response.json();

      return url;
    } catch (err) {
      const status = Number(err?.status || err?.response?.status || 0);
      if (status !== 404 && !shouldSkipNetworkLog(err, status)) {
        await logNetworkError(
          err,
          `${process.env.REACT_APP_SPACES}/${folder}/${key}`,
          {
            action: "retrieveFile",
            folder,
            key,
            status,
          },
          {
            severity: "low",
            category: "spaces",
            type: "spaces-retrieve",
          },
        );
      }
      console.error("Error retrieving file:", err);
      throw err;
    }
  };

  // deleteFile.js - deletes from Spaces service and Mongo if applicable
  const deleteFile = async (folder, key) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SPACES}/file/${folder}/${key}`,
        { method: "DELETE" },
      );
      if (!response.ok) {
        throw new Error(`Delete failed: ${response.statusText}`);
      }
      return true;
    } catch (err) {
      await logNetworkError(
        err,
        `${process.env.REACT_APP_SPACES}/file/${folder}/${key}`,
        {
          action: "deleteFile",
          folder,
          key,
        },
      );
      console.error("Error deleting file:", err);
      throw err;
    }
  };

  const externalData = async (url) => {
    const results = await axiosExternal.get(url);

    return results;
  };

  const postExternalData = async (args) => {
    let formData = new FormData();
    const keys = Object.keys(args.data);
    for (const key of keys) {
      formData.append(key, args.data[key]);
    }

    const results = await axiosExternal.post(args.url, { data: formData });
    return results;
  };

  const getFileUrl = async (args) => {
    let results = { isSuccessful: false, message: "" };

    try {
      const response = await axiosInstance.post(
        requestPath(args),
        { data: JSON.stringify(args.data) },
        { headers: requestHeader(args) },
      );

      results = await serverResponse(response);
    } catch (err) {
      const status = err.response?.status;
      if (!shouldSkipNetworkLog(err, status)) {
        await logNetworkError(err, buildLogEndpoint(args, "getFileUrl"), {
          action: "getFileUrl",
          status,
        });
      }
      console.error("getFileUrl error:", {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
      });
      results.message = err.message || "Failed to get file URL";
    }

    return results;
  };

  // PROCESS INCOMING RESPONSE DATA
  // - convert date strings to date objects
  // =======================================
  const serverResponse = async (response) => {
    if (response.data && response.data.appVersion) {
      setCurrentVersion(response.data.appVersion);
    }

    const data = processResponse(response.data);

    // Global error handling: capture server-declared errors
    if (data && data.isSuccessful === false && data.displayToUser !== false) {
      dispatch(
        errorActions.setError({
          message: data.message,
          fieldErrors: data.fieldErrors,
          severity: data.severity,
          displayToUser: data.displayToUser,
        }),
      );
    }

    return data;
  };

  const processResponse = (obj) => {
    let newResponse;
    if (Array.isArray(obj)) {
      newResponse = [];

      for (const entry of obj) {
        newResponse.push(processResponseItem(entry));
      }
    } else {
      newResponse = processResponseItem(obj);
    }

    return newResponse;
  };

  const processResponseItem = (obj) => {
    if (obj === null || obj === undefined || typeof obj !== "object") {
      return obj;
    }

    let newObj = {};

    let fields = Object.keys(obj);

    // Metadata fields that should not be processed (pass through as-is)
    const metadataFields = [
      "isSuccessful",
      "message",
      "severity",
      "displayToUser",
      "fieldErrors",
      "appVersion",
    ];

    for (const field of fields) {
      // Skip processing metadata fields - they should pass through unchanged
      if (metadataFields.includes(field)) {
        newObj[field] = obj[field];
        continue;
      }

      let value = null;

      if (typeof obj[field] === "object") {
        value = processResponse(obj[field]);
        newObj[field] = value;
      } else if (isISODateString(obj[field])) {
        if (["date", "toDate", "fromDate"].includes(field)) {
          if ("fromTime" in obj && isISODateString(obj["fromTime"])) {
            let newDate = obj[field].substring(0, 10);
            newDate += obj["fromTime"].substring(10);

            newObj[field] = parseISO(newDate);
          } else {
            newObj[field] = parse(
              obj[field].substring(0, 10),
              "yyyy-MM-dd",
              new Date(),
            );
          }
        } else {
          newObj[field] = parseISO(obj[field]);
        }
      } else if (isDate(obj[field])) {
        newObj[field] = new Date(obj[field]);
      } else {
        newObj[field] = obj[field];
      }
    }

    return newObj;
  };

  const isISODateString = (value) => {
    const isoDateFormat =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[-+]\d{2}:?\d{2}|Z)?$/;

    return value && typeof value === "string" && isoDateFormat.test(value);
  };

  return {
    getData,
    postData,
    deleteData,
    externalData,
    uploadFile,
    getFileUrl,
    postExternalData,
    upload,
    retrieveFile,
    deleteFile,
  };
};

export { axiosInstance };
export default useApi;
